// client/src/pages/ManufacturerMap.tsx
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Plus, Merge } from 'lucide-react';
import {
  Facility,
  CreateFacilityRequest,
} from '../services/api/domains/facilities/types';
import {
  useFacilitiesPage,
  useFacilitiesByManufacturer,
  useMergeFacilitiesSuggestions,
  useMergeFacilitiesPreview,
  useMergeFacilities,
} from '../services/api/domains/facilities/hooks';
import { useManufacturersPage } from '../services/api/domains/manufacturers/hooks';
import FacilityModal, { ModalMode } from '../components/modals/FacilityModal';
import GenericMergeModal from '../components/modals/GenericMergeModal';
import { FacilityMergeConflictResolver } from '../components/modals/FacilityMergeConflictResolver';
import useAuth from '../auth/auth';
import TableSearch from 'components/TableSearch';
import {
  SearchMode,
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from 'components/TableSearch/types';
import { facilitiesFieldConfig } from '../config/searchConfigs';
import LgmDataTable, { Column } from 'components/LgmDataTable';
import { useFacilityQTAStatusCounts } from 'services/api/domains/metrics/hooks';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default as any).prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const PAGE_SIZE = 50;

const ManufacturerMap: React.FC = () => {
  const { userId, isViewer, isAdmin } = useAuth();

  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('basic');
  const [basicSearchTerm, setBasicSearchTerm] = useState('');
  const [advancedSearchQuery, setAdvancedSearchQuery] =
    useState<AdvancedSearchQuery>(createEmptyAdvancedQuery());
  const [page, setPage] = useState(0);
  const pageRef = useRef(page);

  // Modal state
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(
    null
  );
  const [openFacilityModal, setOpenFacilityModal] = useState<ModalMode | null>(
    null
  );

  // Merge modal state
  const [openMergeModal, setOpenMergeModal] = useState(false);
  const [mergeSourceFacility, setMergeSourceFacility] =
    useState<Facility | null>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof Facility;
    direction: 'ASC' | 'DESC';
  } | null>(null);

  const facilitiesParams = useMemo(
    () => ({
      search:
        searchMode === 'basic'
          ? basicSearchTerm.trim() || undefined
          : undefined,
      advancedSearch:
        searchMode === 'advanced' ? advancedSearchQuery : undefined,
      sortBy: sortConfig?.key,
      sortDirection: sortConfig?.direction,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [searchMode, basicSearchTerm, advancedSearchQuery, page, sortConfig]
  );

  const {
    data: facilities,
    total,
    refetch: refetchFacilities,
    create: createFacility,
    update: updateFacility,
    delete: deleteFacility,
  } = useFacilitiesPage(facilitiesParams);

  const highLimitParam = useMemo(() => ({ limit: 1000 }), []);

  const { data: manufacturersData } = useManufacturersPage(highLimitParam);

  // Fetch all facilities for the source facility's manufacturer when merge modal is open
  const manufacturerFacilitiesQuery = useFacilitiesByManufacturer(
    mergeSourceFacility?.manufacturer_id || null
  );
  const manufacturerFacilities = useMemo(
    () =>
      manufacturerFacilitiesQuery.status === 'success'
        ? manufacturerFacilitiesQuery.data.facilities
        : [],
    [manufacturerFacilitiesQuery]
  );

  const facilityQTAStatusCounts = useFacilityQTAStatusCounts();

  // Memoized utility functions
  const formatDate = useCallback((date?: Date | string | null) => {
    if (!date || date === null) return '';

    // Handle both Date objects and ISO strings
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return String(date);

    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }, []);

  const getVendorFacilityStatusIconColor = useCallback((f: Facility) => {
    switch (f.facility_status) {
      case 'APPROVED':
        return 'green';
      case 'INACTIVE':
        return 'yellow';
      case 'DISQUALIFY':
        return 'red';
      case 'R&D ONLY':
        return 'yellow';
      case 'ON HOLD':
        return 'yellow';
      case 'NOT MANUF.':
        return 'yellow';
      default:
        return 'gray';
    }
  }, []);

  const getVendorFacilityStatusCSSColor = useCallback((f: Facility) => {
    switch (f.facility_status) {
      case 'APPROVED':
        return { backgroundColor: '#A8E4A0' };
      case 'INACTIVE':
        return { backgroundColor: '#fef3c7' };
      case 'DISQUALIFY':
        return { backgroundColor: '#fee2e2' };
      case 'R&D ONLY':
        return { backgroundColor: '#fef3c7' };
      case 'ON HOLD':
        return { backgroundColor: '#fef3c7' };
      case 'NOT MANUF.':
        return { backgroundColor: '#fef3c7' };
      default:
        return {};
    }
  }, []);

  const makeIcon = useCallback(
    (color: string) =>
      new L.Icon({
        iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
        shadowUrl:
          'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      }),
    []
  );

  const facilitiesColumns = useMemo(
    () =>
      [
        {
          key: 'facility_name',
          label: 'Facility Name',
          render: (row: Facility) => row.facility_name,
          textLength: (row: Facility) => row.facility_name.length,
          width: '15%',
        },
        {
          key: 'manufacturer_name',
          label: 'Manufacturer Name',
          render: (row: Facility) => row.manufacturer_name,
          textLength: (row: Facility) => row.manufacturer_name.length,
          width: '20%',
        },
        {
          key: 'facility_status',
          label: 'Vendor Facility Status',
          tooltip: `Also known as "Manufacturer's Status"`,
        },
        {
          key: 'address_line1',
          label: 'Address Line 1',
        },
        {
          key: 'city',
          label: 'City',
        },
        {
          key: 'postal_code',
          label: 'Postal Code',
        },
        {
          key: 'country_code',
          label: 'Country Code',
        },
      ] as Column<Facility>[],
    []
  );

  // Reset page when search changes
  useEffect(() => {
    if (pageRef.current !== 0) {
      console.log('Resetting page to 0 due to search change');
      setPage(0);
    }
  }, [basicSearchTerm, advancedSearchQuery]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // pagination labels
  const currentPageLength = facilities.length;
  const startIdx = currentPageLength === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIdx =
    currentPageLength === 0 ? 0 : page * PAGE_SIZE + currentPageLength;

  // Memoized handlers for modal actions
  const handleAddFacility = useCallback(() => {
    // Create a new facility with all required fields initialized
    const newFacility: Partial<Facility> = {
      facility_name: '',
      manufacturer_id: -1,
      manufacturer_name: '',
      fei_number: '',
      duns_number: '',
      address_line1: '',
      country_code: '',
      city: '',
      state_province: '',
      postal_code: '',
      created_date: new Date(),
      modified_date: new Date(),
      user_id: userId,
    };
    setSelectedFacility(newFacility as Facility);
    setOpenFacilityModal('EDIT');
  }, [userId]);

  const handleDelete = useCallback(
    async (id?: number) => {
      if (!id || !window.confirm('Delete this facility?')) return;
      try {
        await deleteFacility.mutate(id);
        refetchFacilities();
      } catch (err) {
        console.error('❌ Delete facility failed:', err);
      }
    },
    [deleteFacility, refetchFacilities]
  );

  // New unified modal handlers
  const handleSaveFacility = useCallback(
    async (facilityData: Facility) => {
      try {
        console.log('💾 Saving facility data:', facilityData);

        if (selectedFacility?.facility_id) {
          // Edit existing facility - include user_id
          await updateFacility.mutate({
            ...facilityData,
            user_id: userId,
          });
        } else {
          // Add new facility - validate required fields first
          if (!facilityData.manufacturer_id) {
            console.error(
              '❌ A manufacturer is required for facility creation'
            );
            alert('Please select a manufacturer before saving the facility');
            return;
          }

          if (
            !facilityData.facility_name ||
            facilityData.facility_name.trim() === ''
          ) {
            console.error('❌ facility_name is required for facility creation');
            alert('Please enter a facility name before saving');
            return;
          }

          // Extract required fields for create and include user_id
          const createData: CreateFacilityRequest = {
            manufacturer_id: facilityData.manufacturer_id,
            facility_name: facilityData.facility_name.trim(),
            fei_number: facilityData.fei_number,
            duns_number: facilityData.duns_number,
            address_line1: facilityData.address_line1,
            country_code: facilityData.country_code,
            user_id: userId,
          };

          console.log('🚀 Creating facility with data:', createData);
          await createFacility.mutate(createData);
        }
        setOpenFacilityModal(null);
        setSelectedFacility(null);
        refetchFacilities();
      } catch (err) {
        console.error('❌ Save facility failed:', err);
      }
    },
    [
      selectedFacility,
      updateFacility,
      createFacility,
      refetchFacilities,
      userId,
    ]
  );

  const handleCloseFacilityModal = useCallback(() => {
    setOpenFacilityModal(null);
    setSelectedFacility(null);
  }, []);

  // KPI counts - memoized for performance
  const kpiCounts = useMemo(() => {
    const activeCount =
      facilityQTAStatusCounts.status === 'success'
        ? (facilityQTAStatusCounts.data?.activeCount ?? 0)
        : 0;
    const expiringSoonCount =
      facilityQTAStatusCounts.status === 'success'
        ? (facilityQTAStatusCounts.data?.expiringSoonCount ?? 0)
        : 0;
    const expiredCount =
      facilityQTAStatusCounts.status === 'success'
        ? (facilityQTAStatusCounts.data?.expiredCount ?? 0)
        : 0;

    return { activeCount, expiringSoonCount, expiredCount };
  }, [facilityQTAStatusCounts]);

  const { activeCount, expiringSoonCount, expiredCount } = kpiCounts;

  return (
    <div style={styles.containerStyle}>
      {/* Filter/Search/Count/Add */}
      <div style={styles.filterSectionStyle}>
        <div style={styles.filterControlsStyle}>
          <TableSearch
            mode={searchMode}
            onModeChange={setSearchMode}
            basicSearchTerm={basicSearchTerm}
            onBasicSearchChange={setBasicSearchTerm}
            onBasicSearchClear={() => setBasicSearchTerm('')}
            basicPlaceholder="Search facilities..."
            advancedFieldConfigs={facilitiesFieldConfig}
            advancedQuery={advancedSearchQuery}
            onAdvancedSearch={query => {
              setAdvancedSearchQuery(query);
              setPage(0);
            }}
            onAdvancedClear={() => {
              setAdvancedSearchQuery(createEmptyAdvancedQuery());
              setPage(0);
            }}
          />
        </div>
        <button onClick={handleAddFacility} style={styles.addButtonStyle}>
          <Plus size={16} style={styles.addButtonIconStyle} /> Add Facility
        </button>
      </div>

      {/* Map and KPI Dashboard side by side */}
      <div style={styles.mapKpiRowStyle}>
        <div style={styles.mapContainerWrapperStyle}>
          <MapContainer center={[20, 0]} zoom={2} style={styles.mapContainer}>
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {facilities
              .filter(f => f.latitude && f.longitude)
              .map(f => (
                <Marker
                  key={f.facility_id}
                  position={[f.latitude as number, f.longitude as number]}
                  icon={makeIcon(getVendorFacilityStatusIconColor(f))}>
                  <Popup>
                    <b>{f.facility_name}</b>
                    <br />
                    {f.city}, {f.country_code}
                    <br />
                    QTA Status: {f.qta_status ?? 'N/A'}
                    <br />
                    Vendor Status: {f.facility_status ?? 'N/A'}
                    <br />
                    Effective: {formatDate(f.qta_effective_date)}
                    <br />
                    Exp: {formatDate(f.qta_expiration_date)}
                  </Popup>
                </Marker>
              ))}
          </MapContainer>
        </div>
        <div style={styles.kpiDashboardWrapperStyle}>
          <div style={styles.kpiVerticalStyle}>
            <div style={styles.kpiHeaderStyle}>QTA Expiration Status</div>
            <div style={styles.kpiStyle}>
              <div style={styles.kpiCountStyle}>{activeCount}</div>
              <div>Active</div>
            </div>
            <div style={styles.kpiStyle}>
              <div style={styles.kpiCountStyle}>{expiringSoonCount}</div>
              <div>Expiring within 90 days</div>
            </div>
            <div style={styles.kpiStyle}>
              <div style={styles.kpiCountStyle}>{expiredCount}</div>
              <div>Expired</div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <LgmDataTable<Facility>
          data={facilities}
          columns={facilitiesColumns}
          sortConfig={sortConfig}
          onSort={setSortConfig}
          paginationInfo={{
            startIdx,
            endIdx,
            count: total || 0,
            page,
            setPage,
          }}
          onInfo={(f: Facility) => {
            setSelectedFacility(f);
            setOpenFacilityModal('VIEW');
          }}
          onEdit={
            !isViewer
              ? (f: Facility) => {
                  setSelectedFacility(f);
                  setOpenFacilityModal('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer
              ? (f: Facility) => {
                  handleDelete(f.facility_id);
                }
              : undefined
          }
          actions={
            isAdmin
              ? (f: Facility) => {
                  // Only show merge button if manufacturer has multiple facilities
                  if (f.manufacturer_facility_count <= 1) {
                    return [];
                  }
                  return [
                    {
                      icon: <Merge size={16} />,
                      onAction: () => {
                        setMergeSourceFacility(f);
                        setOpenMergeModal(true);
                      },
                      title: 'Merge Facility',
                      style: { color: '#7c3aed' },
                      hoverStyle: { color: '#5b21b6' },
                    },
                  ];
                }
              : undefined
          }
          customRowStyle={getVendorFacilityStatusCSSColor}
          // customCellStyle={getVendorFacilityCellStatusCSSColor}
        />
      </div>

      {/* FacilityModal */}
      {openFacilityModal && selectedFacility && (
        <FacilityModal
          facility={selectedFacility}
          initialMode={openFacilityModal}
          save={handleSaveFacility}
          close={handleCloseFacilityModal}
          allManufacturers={manufacturersData}
        />
      )}

      {/* GenericMergeModal for Facilities */}
      {openMergeModal && mergeSourceFacility && (
        <GenericMergeModal<Facility>
          source={mergeSourceFacility}
          allEntities={manufacturerFacilities}
          config={{
            domainDisplayName: 'Facility',
            domainDisplayNamePlural: 'Facilities',
            entityIdField: 'facility_id',
            entityNameField: 'facility_name',
            entityCreatedDateField: 'created_date',
            useMergeSuggestions: useMergeFacilitiesSuggestions,
            useMergePreview: useMergeFacilitiesPreview,
            useMerge: useMergeFacilities,
            renderConflicts: (previewData, resolvedFields, onFieldResolve) => (
              <FacilityMergeConflictResolver
                previewData={previewData}
                resolvedFields={resolvedFields}
                onFieldResolve={onFieldResolve}
              />
            ),
          }}
          onClose={() => {
            setOpenMergeModal(false);
            setMergeSourceFacility(null);
          }}
          onSuccess={() => {
            setOpenMergeModal(false);
            setMergeSourceFacility(null);
            refetchFacilities();
          }}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  kpiVerticalStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '1rem',
    alignItems: 'center', // Center horizontally
  },
  // Row for map and KPI dashboard
  mapKpiRowStyle: {
    display: 'flex',
    flexDirection: 'row',
    gap: '2rem', // space between map and KPI dashboard
    alignItems: 'flex-start',
    width: '100%',
  },
  kpiDashboardWrapperStyle: {
    minWidth: '320px',
    maxWidth: '400px',
    flex: '0 0 350px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    height: '500px', // match map height
  },
  // Main container styles
  containerStyle: {
    padding: '1rem', // p-4
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem', // space-y-6
  },

  // KPI Dashboard styles
  kpiGridStyle: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', // grid-cols-5
    gap: '1rem', // gap-4
    marginBottom: '1rem', // mb-4
  },
  kpiTotalStyle: {
    backgroundColor: '#3b82f6', // bg-blue-500
    color: 'white', // text-white
    padding: '1rem', // p-4
    borderRadius: '0.5rem', // rounded
    textAlign: 'center',
  },
  kpiNumberStyle: {
    fontSize: '2rem', // text-3xl
    fontWeight: 'bold', // font-bold
  },
  kpiStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  kpiCountStyle: {
    fontSize: '3rem', // text-sm
    fontWeight: 'bold', // font-semibold
  },
  kpiHeaderStyle: {
    fontSize: '1rem', // text-sm
    fontWeight: 'bold', // font-semibold
  },
  kpiLabelStyle: {
    fontSize: '0.875rem', // text-sm
  },

  // Filter section styles
  filterSectionStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between', // justify-between
  },
  filterControlsStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem', // space-x-4
  },
  searchInputStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
    borderRadius: '0.25rem', // rounded
  },
  addButtonStyle: {
    backgroundColor: '#2563eb', // bg-blue-600
    color: 'white', // text-white
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },

  // Table styles
  tableStyle: {
    width: '100%', // w-full
    borderCollapse: 'collapse',
    backgroundColor: 'white', // bg-white
    borderRadius: '0.5rem', // rounded
    overflow: 'hidden',
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // shadow
  },
  tableHeaderStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
  },
  tableHeaderCellStyle: {
    padding: '0.75rem', // p-3
    textAlign: 'left',
    fontSize: '0.875rem', // text-sm
    fontWeight: '600', // font-semibold
    color: '#374151', // text-gray-700
    borderBottom: '1px solid #e5e7eb', // border-b
  },
  tableCellStyle: {
    padding: '0.75rem', // p-3
    borderBottom: '1px solid #f3f4f6', // border-b border-gray-100
    fontSize: '0.875rem', // text-sm
  },
  tableRowHoverStyle: {
    backgroundColor: '#f9fafb', // hover:bg-gray-50
  },
  actionButtonStyle: {
    padding: '0.25rem 0.5rem', // px-2 py-1
    margin: '0 0.125rem', // mx-0.5
    fontSize: '0.75rem', // text-xs
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  viewButtonStyle: {
    backgroundColor: '#3b82f6', // bg-blue-500
    color: 'white', // text-white
  },
  editButtonStyle: {
    backgroundColor: '#f59e0b', // bg-yellow-500
    color: 'white', // text-white
  },

  // Modal styles
  modalOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // bg-black bg-opacity-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000, // Increased to ensure it appears above Leaflet map
  },
  modalContentStyle: {
    backgroundColor: 'white', // bg-white
    padding: '1.5rem', // p-6
    borderRadius: '0.5rem', // rounded
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // shadow-xl
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  modalTitleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold', // font-bold
    marginBottom: '1rem', // mb-4
    color: '#1f2937', // text-gray-800
  },

  // Form styles
  formInputStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
    marginBottom: '0.5rem', // mb-2
    width: '100%', // w-full
    borderRadius: '0.25rem', // rounded
  },
  formTextareaStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
    marginBottom: '0.5rem', // mb-2
    width: '100%', // w-full
    borderRadius: '0.25rem', // rounded
    minHeight: '80px',
    resize: 'vertical',
  },

  // Button styles
  buttonContainerStyle: {
    display: 'flex',
    justifyContent: 'flex-end', // justify-end
    gap: '0.5rem', // space-x-2
    marginTop: '1rem', // mt-4
  },
  saveButtonStyle: {
    backgroundColor: '#2563eb', // bg-blue-600
    color: 'white', // text-white
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  },
  cancelButtonStyle: {
    backgroundColor: '#d1d5db', // bg-gray-300
    color: '#374151', // text-gray-700
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  },

  // Map container styles
  mapContainerWrapperStyle: {
    width: '100%', // w-full
    height: '500px', // h-[500px]
  },
  mapContainer: {
    height: '100%',
    width: '100%',
  },

  // Additional table styles
  tableRowStyle: {
    backgroundColor: 'white',
  },
  tableRowExpiringStyle: {
    backgroundColor: '#fef3c7', // bg-yellow-100 for expiring items
  },
  tableCellActionStyle: {
    padding: '0.5rem', // p-2
    borderBottom: '1px solid #f3f4f6', // border-b border-gray-100
    fontSize: '0.875rem', // text-sm
    display: 'flex',
    gap: '0.5rem', // space-x-2
  },
  deleteButtonStyle: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#dc2626', // text-red-600
    cursor: 'pointer',
    padding: '0.25rem',
  },

  // Missing styles referenced in component
  kpiPendingStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
    color: '#000000', // text-black
    padding: '0.75rem', // p-3
    borderRadius: '0.5rem', // rounded-lg
    textAlign: 'center',
  },
  filterSelectStyle: {
    border: '1px solid #d1d5db', // border
    padding: '0.5rem', // p-2
  },
  facilityCountStyle: {
    fontSize: '0.875rem', // text-sm
    color: '#4b5563', // text-gray-600
  },
  addButtonIconStyle: {
    marginRight: '0.5rem', // mr-2
  },
};

export default ManufacturerMap;
